
import React, { useState, useEffect } from 'react';
import { HistoryItem } from '../types';
import { Watermark, addWatermarkToImage, generatedImageDownloadFilename } from './Watermark';
import type { Lang } from '../utils/i18n';

interface HistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  items: HistoryItem[];
  onSelectItem: (item: HistoryItem) => void;
  onDeleteItem: (id: string) => void;
  isDarkMode: boolean;
  language: Lang;
}

const HistoryPanel: React.FC<HistoryPanelProps> = ({ isOpen, onClose, items, onSelectItem, onDeleteItem, isDarkMode, language }) => {
  console.log('[HistoryPanel] 🔍🔍🔍 组件渲染检查 🔍🔍🔍');
  console.log('[HistoryPanel] isOpen 值:', isOpen);
  console.log('[HistoryPanel] items.length:', items.length);
  console.log('[HistoryPanel] isDarkMode:', isDarkMode);
  console.log('[HistoryPanel] language:', language);
  
  // 🔥 跟踪图片加载失败的记录ID
  const [failedImageIds, setFailedImageIds] = useState<Set<string>>(new Set());
  
  // 🔥 监听 isOpen 变化，输出调试信息
  useEffect(() => {
    if (isOpen) {
      console.log('[HistoryPanel] ✅✅✅ 面板状态变为打开 ✅✅✅');
      console.log('[HistoryPanel] 当前历史记录数量:', items.length);
    } else {
      console.log('[HistoryPanel] ❌ 面板状态变为关闭');
    }
  }, [isOpen, items.length]);
  
  if (!isOpen) {
    console.log('[HistoryPanel] ❌ isOpen 为 false，不显示面板');
    return null;
  }

  console.log('[HistoryPanel] ✅✅✅ isOpen 为 true，应该显示面板 ✅✅✅');
  const t = (en: string, zh: string) => language === 'en' ? en : zh;

  // 🔥 处理图片加载失败
  const handleImageError = (itemId: string) => {
    console.warn(`[HistoryPanel] ⚠️ 图片加载失败: ${itemId}`);
    setFailedImageIds(prev => new Set(prev).add(itemId));
  };

  const handleDownload = async (e: React.MouseEvent, item: HistoryItem) => {
    e.stopPropagation(); 
    
    try {
      // 添加水印到图片
      const watermarkedImage = await addWatermarkToImage(item.renderedImage);
      
      // 创建下载链接
      const link = document.createElement('a');
      link.href = watermarkedImage;
      link.download = generatedImageDownloadFilename(item.id);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download with watermark failed:', error);
      alert(
        t(
          'Download failed. Sign in and retry if the image is stored remotely.',
          '下载失败，请登录账号后重试（远程图片需后端拉取才能加水印）。',
        ),
      );
    }
  };

  const getDaysLeftLabel = (timestamp: number) => {
    const expireAt = timestamp + 7 * 24 * 60 * 60 * 1000;
    const leftMs = expireAt - Date.now();
    const leftDays = Math.max(0, Math.ceil(leftMs / (24 * 60 * 60 * 1000)));

    if (language === 'en') {
      return leftDays <= 0 ? 'Delete today' : `${leftDays} day${leftDays > 1 ? 's' : ''} left`;
    }
    return leftDays <= 0 ? '今日删除' : `剩余${leftDays}天删除`;
  };

  console.log('[HistoryPanel] 🎨 开始渲染面板 DOM');
  
  return (
    <div 
      className="fixed inset-0 z-[2500] flex justify-end"
      style={{
        zIndex: 2500, // 🔥 提高 z-index，确保面板在 PaymentCenter (3000) 和 UpscalePanel (2000) 之下，但在其他元素之上
        position: 'fixed', // 🔒🔒🔒 永久锁死：确保面板固定定位，禁止修改 🔒🔒🔒
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: 'auto', // 🔒🔒🔒 永久锁死：确保可以交互，禁止修改 🔒🔒🔒
      }}
    >
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" 
        onClick={onClose}
        style={{
          pointerEvents: 'auto', // 🔒🔒🔒 永久锁死：确保背景可以点击关闭，禁止修改 🔒🔒🔒
        }}
      />
      <div 
        className={`relative w-[400px] h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300 transition-colors bg-transparent border-l ${isDarkMode ? 'border-white/10' : 'border-black/5'}`}
        style={{
          zIndex: 2501, // 🔥 提高 z-index，确保面板内容在背景之上
          pointerEvents: 'auto', // 🔒🔒🔒 永久锁死：确保面板内容可以交互，禁止修改 🔒🔒🔒
          backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.95)', // 🔒 确保面板有背景色，可见
        }}
      >
        <div className={`p-8 border-b flex items-center justify-between border-white/5`}>
          <div>
            <h2 className={`text-xl font-black italic tracking-tighter text-white`}>
              {t('RENDER', '渲染')} <span className="text-[#D7CDA1]">{t('HISTORY', '历史')}</span>
            </h2>
            <p className="text-[10px] text-gray-500 font-bold tracking-widest uppercase mt-1">
              {t('Stored in Database', '数据库存储')} • {items.length} {t('Items', '记录')}
            </p>
            <p className="mt-1.5 max-w-[240px] text-[10px] leading-snug text-red-500">
              {t('Stored for 7 days — please download in time.', '存储时间为7天请及时下载')}
            </p>
          </div>
          <button onClick={onClose} className={`p-2 rounded-full transition-all hover:bg-white/5 text-gray-400 hover:text-white`}>
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center opacity-30 grayscale">
              <p className="text-sm font-bold tracking-widest uppercase text-white">
                {t('NO HISTORY FOUND', '暂无历史记录')}
              </p>
            </div>
          ) : (
            items.map((item) => {
              const isUpscale = item.id.startsWith('upscale-');
              const imageFailed = failedImageIds.has(item.id);
              return (
                <div 
                  key={item.id}
                  className={`group relative overflow-hidden border transition-all cursor-pointer shadow-lg bg-white/5 border-white/5 hover:border-[#D7CDA1]/30`}
                  style={{ borderRadius: '12px' }} // rounded-2xl (16px) 减少20%再减少10% = 11.7px ≈ 12px
                  onClick={() => onSelectItem(item)}
                >
                  <div className="aspect-video relative overflow-hidden bg-gray-800/50">
                    <div className="absolute top-3 right-3 z-30 px-2 py-1 rounded-md bg-black/65 border border-white/20">
                      <span className="text-[10px] font-bold tracking-wide text-[#ffb4b4]">
                        {getDaysLeftLabel(item.timestamp)}
                      </span>
                    </div>
                    {imageFailed ? (
                      // 🔥 图片加载失败时显示占位符
                      <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                        <svg className="w-16 h-16 mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-xs font-bold uppercase tracking-wider opacity-60">
                          {t('Image Unavailable', '图片不可用')}
                        </p>
                        <p className="text-[10px] mt-1 opacity-40">
                          {t('Click to restore session', '点击恢复会话')}
                        </p>
                      </div>
                    ) : (
                      <img 
                        src={item.renderedImage} 
                        alt="Render" 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                        onError={() => handleImageError(item.id)}
                        loading="lazy"
                      />
                    )}

                    {/* Upscale 放大镜图标 - 仅在图片加载成功时显示 */}
                    {!imageFailed && isUpscale && (
                      <div className="absolute top-3 left-3 z-30 w-8 h-8 bg-[#D7CDA1] text-[#101820] rounded-full flex items-center justify-center border border-white/20">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                      </div>
                    )}

                    {/* 水印 - 仅在图片加载成功时显示 */}
                    {!imageFailed && (
                      <div className="absolute bottom-1 right-1 z-[15] pointer-events-none">
                        <Watermark size="small" />
                      </div>
                    )}

                    {/* Hover 提示 - 仅在图片加载成功时显示 */}
                    {!imageFailed && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                        <span className="text-[10px] font-black text-[#D7CDA1] tracking-[0.2em]">
                          {t('RESTORE SESSION', '恢复会话')}
                        </span>
                      </div>
                    )}
                    
                    {/* 图片加载失败时的提示 - 始终显示在占位符上方 */}
                    {imageFailed && (
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                        <span className="text-[10px] font-black text-[#D7CDA1] tracking-[0.2em]">
                          {t('RESTORE SESSION', '恢复会话')}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-gray-400">
                        {new Date(item.timestamp).toLocaleDateString()} • {new Date(item.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span className={`text-[9px] font-black uppercase mt-0.5 tracking-widest ${isUpscale ? 'text-[#D7CDA1]' : 'text-white/40'}`}>
                        {isUpscale 
                          ? t('AI UPSCALE', 'AI 高清放大') 
                          : `${item.fixtures.filter(f => f.isModified).length} ${t('LIGHTS MODIFIED', '处灯光已修改')}`}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button 
                        onClick={(e) => handleDownload(e, item)} 
                        className={`p-2 transition-all hover:bg-[#D7CDA1]/20 text-gray-400 hover:text-[#D7CDA1]`}
                        style={{ borderRadius: '5px' }} // rounded-lg (8px) 减少20%再减少10% = 5.4px ≈ 5px
                        title={t('Download Image', '下载图片')}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); onDeleteItem(item.id); }} 
                        className={`p-2 transition-all hover:bg-red-500/20 text-gray-400 hover:text-red-500`}
                        style={{ borderRadius: '5px' }} // rounded-lg (8px) 减少20%再减少10% = 5.4px ≈ 5px
                        title={t('Delete Record', '删除记录')}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoryPanel;
